// CloudTrail Portal API Service
import { API_BASE_URL } from '../utils/constants';

// Get CloudTrail token from localStorage
const getCloudTrailToken = () => {
  return localStorage.getItem('cloudtrail_token');
};

// Set CloudTrail token in localStorage
export const setCloudTrailToken = (token) => {
  localStorage.setItem('cloudtrail_token', token);
};

// Remove CloudTrail token from localStorage
export const removeCloudTrailToken = () => {
  localStorage.removeItem('cloudtrail_token');
  localStorage.removeItem('cloudtrail_user');
};

// Get CloudTrail user from localStorage
export const getCloudTrailUser = () => {
  const user = localStorage.getItem('cloudtrail_user');
  return user ? JSON.parse(user) : null;
};

// Set CloudTrail user in localStorage
export const setCloudTrailUser = (user) => {
  localStorage.setItem('cloudtrail_user', JSON.stringify(user));
};

// Parse JWT token to get claims
export const parseCloudTrailToken = (token) => {
  if (!token) return null;
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    console.error('Error parsing CloudTrail JWT token:', e);
    return null;
  }
};

// Generic fetch wrapper for CloudTrail API calls
const cloudtrailFetch = async (endpoint, options = {}) => {
  const token = getCloudTrailToken();

  const defaultHeaders = {
    'Content-Type': 'application/json',
  };

  if (token) {
    defaultHeaders['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers: {
      ...defaultHeaders,
      ...options.headers,
    },
  });

  if (response.status === 401) {
    removeCloudTrailToken();
    window.location.href = '/cloudtrail';
    throw new Error('Authentication required');
  }

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.message || data.error || 'Request failed');
  }

  return data;
};

// CloudTrail API functions

// Get current user info
export const getCloudTrailMe = () => {
  return cloudtrailFetch('/api/v1/cloudtrail/me');
};

// List AWS accounts available for trail creation
export const listCloudTrailAccounts = () => {
  return cloudtrailFetch('/api/v1/cloudtrail/accounts');
};

// Create a new CloudTrail trail
export const createTrail = (data) => {
  return cloudtrailFetch('/api/v1/cloudtrail/trails', {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

// List all trails
export const listTrails = () => {
  return cloudtrailFetch('/api/v1/cloudtrail/trails');
};

// Get trail detail
export const getTrail = (trailId) => {
  return cloudtrailFetch(`/api/v1/cloudtrail/trails/${trailId}`);
};

// Update a trail
export const updateTrail = (trailId, data) => {
  return cloudtrailFetch(`/api/v1/cloudtrail/trails/${trailId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
};

// Delete a trail
export const deleteTrail = (trailId, deleteS3Bucket = false) => {
  return cloudtrailFetch(`/api/v1/cloudtrail/trails/${trailId}`, {
    method: 'DELETE',
    body: JSON.stringify({ deleteS3Bucket }),
  });
};

// Add accounts to an existing trail
export const addTrailAccounts = (trailId, accountIds) => {
  return cloudtrailFetch(`/api/v1/cloudtrail/trails/${trailId}/accounts`, {
    method: 'POST',
    body: JSON.stringify({ accountIds }),
  });
};

// Remove accounts from an existing trail
export const removeTrailAccounts = (trailId, accountIds) => {
  return cloudtrailFetch(`/api/v1/cloudtrail/trails/${trailId}/accounts`, {
    method: 'DELETE',
    body: JSON.stringify({ accountIds }),
  });
};
